/**
 * Shared responsive row for the broad list surfaces (2026-08-14).
 *
 * ONE source of truth for the ranked product row rendered by both the SSR
 * /c/list page (`BroadListView`, a crawlable <Link>) and the inline
 * fast-results page (`FastResults`, a client <button>). Before this, the row
 * markup was copy-pasted into both files and drifted; it was also a rigid
 * 4-across flex built for the 768px desktop width, so on a phone the fixed
 * 80px thumbnail + ~100px price column + rank pill + gaps starved the product
 * name to ~23px at 360px (measured), collapsing the title to two letters and
 * jamming the shop line against the CTA.
 *
 * The row now reflows:
 *   - Mobile (< sm): two stacked rows. Row 1 is the thumbnail + the full-width
 *     identity, so the title wraps to two lines
 *     instead of truncating. Row 2 is a divided price/CTA bar with `from $X` and
 *     `Get best price ->` as a wide tap target.
 *   - Desktop (>= sm): the wrapper is `sm:flex` and the mobile grouping div is
 *     `sm:contents`, so the children collapse back to the ORIGINAL 4-across
 *     single-line row, pixel-for-pixel unchanged.
 *
 * Pure server component (no hooks), so it renders inside both the server
 * BroadListView and the client FastResults. ProductThumb stays a client
 * component for its onError fallback.
 */

import type { ReactNode } from "react";

import type { BroadProduct } from "@/lib/api/broad";

import { ProductThumb } from "./ProductThumb";

/**
 * Classes for the wrapping <Link>/<button>. Mobile is a block stack; `sm:flex`
 * restores today's horizontal row. Everything else (radius, border, surface,
 * padding, hover) is byte-for-byte the original wrapper.
 */
export const LIST_CARD_CLASS =
  "group relative block w-full rounded-2xl border border-border bg-secondary " +
  "p-4 text-left transition hover:border-border hover:bg-secondary " +
  "sm:flex sm:items-stretch sm:gap-4";

export function ListCardContent({
  product: p,
  statusChip,
}: {
  product: BroadProduct;
  /** Optional availability chip, rendered INSIDE the body below the rating (the
   *  approved Show-Then-Verify placement). Only the streamed category view passes
   *  it; the SSR /c/list and blocking fast-results surfaces omit it, so their
   *  output stays byte-for-byte identical. */
  statusChip?: ReactNode;
}) {
  return (
    <>
      {/* Mobile row 1 = thumb + identity. `sm:contents` dissolves this wrapper on
          desktop so the thumb and text become direct flex children again. The
          rank number was removed per owner request (2026-08-14); the JSON-LD
          ItemList still carries `position` independently, so SEO is unaffected. */}
      <div className="flex items-start gap-3 sm:contents">
        <ProductThumb src={p.image} alt={p.title} />

        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <div className="truncate text-[12px] uppercase tracking-wider text-muted-foreground">
            {p.brand}
          </div>
          <h3 className="mt-0.5 line-clamp-2 text-base font-semibold leading-tight text-foreground sm:line-clamp-1">
            {p.title}
          </h3>
          <div className="mt-1 text-sm text-muted-foreground">
            {p.rating ? (
              <>
                <span className="text-amber-300">&#9733;</span> {p.rating}
                {p.reviews_cnt ? ` · ${p.reviews_cnt} reviews` : ""} · at{" "}
                {p.shop}
              </>
            ) : (
              <>at {p.shop}</>
            )}
          </div>

          {statusChip}
        </div>
      </div>

      {/* Price + CTA. Mobile: a divided full-width bar. Desktop: the original
          right-hand column. */}
      <div className="mt-3 flex shrink-0 items-center justify-between gap-2 border-t border-border/60 pt-3 sm:mt-0 sm:flex-col sm:items-end sm:justify-center sm:gap-1 sm:border-0 sm:pl-2 sm:pt-0">
        <div className="flex items-baseline gap-1.5 sm:flex-col sm:items-end sm:gap-1">
          <span className="text-[12px] uppercase tracking-wider text-muted-foreground">
            from
          </span>
          <span className="text-lg font-semibold tabular-nums text-foreground">
            {p.price_display}
          </span>
        </div>
        <span className="text-xs font-medium text-amber-300/90">
          Get best price →
        </span>
      </div>
    </>
  );
}
